import * as stylex from "@stylexjs/stylex";
import { useCanGoBack, useLocation, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import {
  IconButton,
  SearchField,
  SearchRightStrokeIcon,
  TextButton,
  useMediaQuery,
  useDebounce
} from "@/libs/serenity-ui";
import { breakpoints } from "@/libs/serenity-ui/constants.stylex";
import { $ } from "@/libs/serenity-ui/tokens.stylex";

interface SearchBarProps {
  isCompact?: boolean;
}

export function SearchBar({ isCompact = false }: SearchBarProps) {
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const location = useLocation();

  const isTablet = useMediaQuery(breakpoints.medium);

  const [isCompactSearchBarVisible, setIsCompactSearchBarVisible] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const navigate = useNavigate();

  useEffect(() => {
    if (debouncedSearchTerm !== '') {
      navigate({
        to: '/search/$tab',
        replace: location.pathname.includes('/search/'),
        params: (prev) => ({ tab: prev.tab ?? 'articles' }),
        search: { q: debouncedSearchTerm }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  useEffect(() => {
    setSearchTerm(location.search.q ?? '');
  }, [location.pathname, location.search.q]);

  return isCompact ?
    <>
      <IconButton aria-label="Search" onPress={() => setIsCompactSearchBarVisible(true)}>
        <SearchRightStrokeIcon />
      </IconButton>
      {isCompactSearchBarVisible &&
        <div {...stylex.props(compactStyles.base)}>
          <SearchField
            autoFocus
            size={44}
            style={compactStyles.searchBar}
            value={searchTerm}
            variant="filled"
            onChange={setSearchTerm}
          />
          <TextButton
            size={48}
            onPress={() => {
              setIsCompactSearchBarVisible(false);
              setSearchTerm('');
              void (canGoBack ? router.history.back() : navigate({ to: '/' }));
            }}>
            Cancel
          </TextButton>
        </div>}
    </> :
    <SearchField
      style={isTablet ? styles.mediumWidth : styles.base}
      value={searchTerm}
      onChange={(value) => {
        setSearchTerm(value ?? '');

        if (!value) {
          void (canGoBack ? router.history.back() : navigate({ to: '/' }));
        }
      }}
    />;
}

const styles = stylex.create({
  base: {
    width: 464,
  },
  mediumWidth: {
    width: 364,
  },
});

const compactStyles = stylex.create({
  base: {
    alignItems: 'center',
    backgroundColor: $.surfaceBGPrimary,
    display: 'flex',
    gap: '16px',
    left: 0,
    paddingInline: 16,
    paddingTop: 10,
    position: 'absolute',
    top: 0,
    width: '100%',
    zIndex: 2,
  },
  searchBar: {
    flexGrow: 1,
  }
});
